import { Button, ModalV2, Text } from 'components';
import { theme } from 'lib';
import { useEffect, useState } from 'react';
import { LEVEL_LABELS, PROFANITY_LEVELS } from '../constants';

const inputCls =
  'h-[38px] w-full border border-[#D2D8DF] rounded-lg px-3 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20 disabled:bg-[#F0F2F4] disabled:text-[#97A3B5]';
const labelCls = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';

/** Add / edit a profanity term. `term` prop set = edit mode (term text is identity, locked). */
const TermModal = ({ visible, term, onClose, onSubmit, submitting }) => {
  const isEdit = !!term;
  const [text, setText] = useState('');
  const [level, setLevel] = useState('medium');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible) {
      setText(term?.term || '');
      setLevel(term?.level || 'medium');
      setError(null);
    }
  }, [visible, term]);

  const handleSubmit = () => {
    const trimmed = text.trim().toLowerCase();
    if (!trimmed) {
      setError('Term is required.');
      return;
    }
    if (trimmed.length > 100) {
      setError('Term must be 100 characters or fewer.');
      return;
    }
    onSubmit({ term: trimmed, level });
  };

  return (
    <ModalV2
      visible={visible}
      closeModal={onClose}
      width="420px"
      header={
        <Text h4 bold marg="0">
          {isEdit ? 'Edit term' : 'Add term'}
        </Text>
      }
    >
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <label className={labelCls}>Term</label>
          <input
            type="text"
            value={text}
            disabled={isEdit}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && !submitting && handleSubmit()}
            placeholder="word or phrase"
            className={`${inputCls} font-mono`}
            autoFocus={!isEdit}
          />
          {isEdit && (
            <Text c1 col={theme.GREY_50} marg="2px 0 0" style={{ fontSize: '11px' }}>
              The term itself can't be changed — delete it and add a new one instead.
            </Text>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelCls}>Level</label>
          <select value={level} onChange={(e) => setLevel(e.target.value)} className={inputCls}>
            {PROFANITY_LEVELS.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABELS[l]}
              </option>
            ))}
          </select>
          <Text c1 col={theme.GREY_50} marg="2px 0 0" style={{ fontSize: '11px' }}>
            Low: allowed · Medium: allowed, audited · High: blocked instantly
          </Text>
        </div>

        {error && (
          <Text c1 col={theme.SECONDARY} marg="0" style={{ fontSize: '12px' }}>
            {error}
          </Text>
        )}

        <div className="flex justify-end items-center gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-[32px] px-4 rounded-full border border-[#D2D8DF] text-[13px] text-[#5F6F86] font-medium hover:bg-[#F0F2F4] transition-colors"
          >
            Cancel
          </button>
          <Button
            text={isEdit ? 'Save' : 'Add term'}
            bg={theme.SECONDARY}
            small
            wid="120px"
            loading={submitting}
            disabled={submitting}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </ModalV2>
  );
};

export default TermModal;
